
import { useState, useEffect } from "react"
import { Modal } from "react-bootstrap"
import Select from "react-select"
import PropTypes from "prop-types"
import Swal from "sweetalert2"

const FollowUpForm = ({
  showFollowUpForm,
  setShowFollowUpForm,
  followupOptions,
  CustomOption,
  CustomSingleValue,
  prescriptionId,
  patientId,
}) => {
  const [questionNotes, setQuestionNotes] = useState({})
  const [isScheduledFollowup, setIsScheduledFollowup] = useState(false)
  const [selectedScheduleReason, setSelectedScheduleReason] = useState(null)
  const [showDoctorSelect, setShowDoctorSelect] = useState({})
  const [selectedDoctorId, setSelectedDoctorId] = useState(null)
  const [showNotesModal, setShowNotesModal] = useState(false)
  const [noteText, setNoteText] = useState("")
  const [isImportant, setIsImportant] = useState(false)
  const [isEmergency, setIsEmergency] = useState(false)
  const [selectedDoctorForNote, setSelectedDoctorForNote] = useState(null)
  const [questionData, setQuestionData] = useState([])
  const [addNoteLoading, setAddNoteLoading] = useState(false)
  const [notes, setNotes] = useState([])

  const staticAnswers = [
    {
      question_answer_id: 1,
      question: "How are you going to do consulting?",
      answer: "Nashik Center",
      isGroup: "No",
    },
    {
      question_answer_id: 2,
      question: "How do you feel hungry?",
      answer: "More",
      isGroup: "No",
    },
    {
      question_answer_id: 3,
      question: "When do you have breakfast?",
      answer: "Afternoon",
      isGroup: "No",
    },
    {
      group_id: 1,
      group_name: "Dietary Habits",
      isGroup: "Yes",
      questions: [
        {
          question_answer_id: 4,
          question: "What is your preferred meal time?",
          answer: "Evening",
        },
        {
          question_answer_id: 5,
          question: "How many glasses of water do you drink daily?",
          answer: "6-8 glasses",
        },
      ],
    },
  ]

  const staticDoctors = [
    {
      doctor_id: 1,
      doctor_name: "Dr. John Smith",
      specialization: "Cardiologist",
    },
    {
      doctor_id: 2,
      doctor_name: "Dr. Sarah Johnson",
      specialization: "General Physician",
    },
    {
      doctor_id: 3,
      doctor_name: "Dr. Michael Brown",
      specialization: "Neurologist",
    },
  ]

  const staticFollowupIdData = {
    followup_id: "FU_" + Date.now(),
    followupIdLoading: false,
    followupIdError: null,
  }

  useEffect(() => {
    if (showFollowUpForm) {
      // Initialize question data
      const initialData = []

      // Handle standalone questions
      const standaloneQuestions = staticAnswers.filter((item) => item.isGroup === "No")
      standaloneQuestions.forEach((answer) => {
        initialData.push({
          question_answer_id: answer.question_answer_id,
          notes: "",
          isImportant: false,
          isEmergency: false,
          doctor_id: null,
        })
      })

      // Handle grouped questions
      const groupedQuestions = staticAnswers.filter((item) => item.isGroup === "Yes")
      groupedQuestions.forEach((group) => {
        if (group.questions) {
          group.questions.forEach((question) => {
            initialData.push({
              question_answer_id: question.question_answer_id,
              notes: "",
              isImportant: false,
              isEmergency: false,
              doctor_id: null,
            })
          })
        }
      })

      setQuestionData(initialData)

      // Reset form state
      setIsScheduledFollowup(false)
      setSelectedScheduleReason(null)
      setShowDoctorSelect({})
      setSelectedDoctorId(null)
      setNoteText("")
      setIsImportant(false)
      setIsEmergency(false)
      setSelectedDoctorForNote(null)
      setNotes([])
    }
  }, [showFollowUpForm])

  const handleDoctorChange = (selectedOption) => {
    setSelectedDoctorId(selectedOption?.value || null)
  }

  const handleCheckboxChange = (event) => {
    const checkboxId = event.target.id
    const qIndex = checkboxId.split("-")[1]
    const form = event.target.closest("form") || document
    const isImportantChecked = form.querySelector(`input[id="importantConsulting-${qIndex}"]:checked`) !== null
    const isEmergencyChecked = form.querySelector(`input[id="emergencyConsulting-${qIndex}"]:checked`) !== null

    setShowDoctorSelect((prev) => ({
      ...prev,
      [qIndex]: isImportantChecked || isEmergencyChecked,
    }))
  }

  const handleNotesChange = (qIndex, value) => {
    setQuestionData((prevData) => {
      const newData = [...prevData]
      const questionItem = newData.find((item) => item.question_answer_id === qIndex)
      if (questionItem) {
        questionItem.notes = value
      }
      return newData
    })
  }

  const handleSaveNote = async () => {
    if (!noteText.trim()) {
      Swal.fire({
        title: "Error",
        text: "Please enter note description",
        icon: "warning",
        confirmButtonText: "OK",
      })
      return
    }

    setAddNoteLoading(true)

    // Simulate API call
    setTimeout(() => {
      const newNote = {
        note_description: noteText,
        created_at: new Date().toLocaleDateString(),
        is_important: isImportant ? "Yes" : "No",
        is_emergency: isEmergency ? "Yes" : "No",
        doctor_id: selectedDoctorForNote?.value || "0",
      }

      setNotes((prev) => [...prev, newNote])
      setNoteText("")
      setIsImportant(false)
      setIsEmergency(false)
      setSelectedDoctorForNote(null)
      setShowNotesModal(false)
      setAddNoteLoading(false)

      Swal.fire({
        title: "Success",
        text: "Note added successfully",
        icon: "success",
        confirmButtonText: "OK",
      })
    }, 1000)
  }

  const handleSubmit = async () => {
    const followupForm = questionData.filter((q) => q.notes.trim() !== "")

    if (followupForm.length === 0) {
      Swal.fire({
        title: "Error",
        text: "Please add at least one note before submitting.",
        icon: "warning",
        confirmButtonText: "OK",
      })
      return
    }

    // Simulate API call
    setTimeout(() => {
      Swal.fire({
        title: "Success",
        text: "Followup added successfully",
        icon: "success",
        confirmButtonText: "OK",
      })

      // Reset form state
      setQuestionData([])
      setNoteText("")
      setIsScheduledFollowup(false)
      setSelectedScheduleReason(null)
      setShowDoctorSelect({})
      setSelectedDoctorId(null)
      setShowFollowUpForm(false)
    }, 1500)
  }

  const handleDiscard = async () => {
    setIsScheduledFollowup(false)
    setSelectedScheduleReason(null)
    setShowDoctorSelect({})
    setSelectedDoctorId(null)
    setShowFollowUpForm(false)
  }

  const selectedDoctor = staticDoctors.find((doc) => doc.doctor_id === selectedDoctorId)
  const selectedDoctorValue = selectedDoctor
    ? {
        value: selectedDoctor.doctor_id,
        label: selectedDoctor.doctor_name,
        specialization: selectedDoctor.specialization,
      }
    : null

  const formatOptionLabel = ({ label, specialization }) => (
    <div>
      <div style={{ fontWeight: "bold" }}>{label}</div>
      <div style={{ fontSize: "0.8em", color: "#666" }}>{specialization}</div>
    </div>
  )

  if (!showFollowUpForm) return null

  const standaloneQuestions = staticAnswers.filter((item) => item.isGroup === "No")
  const groupedQuestions = staticAnswers.filter((item) => item.isGroup === "Yes")

  const renderQuestion = (item) => (
    <div key={item.question_answer_id} className="col-xl-12 mb-3">
      <div className="timeline-panel bgl-dark flex-wrap border-0 p-3 rounded d-flex justify-content-between align-items-center">
        <div>
          <h6 className="font-weight-bold">{item.question || "No question text"}</h6>
          <p className="fs-15 mb-0 font-weight-bold">{item.answer}</p>
          <textarea
            className="form-control mt-2"
            placeholder="Enter Notes here.."
            value={questionData.find((q) => q.question_answer_id === item.question_answer_id)?.notes || ""}
            onChange={(e) => handleNotesChange(item.question_answer_id, e.target.value)}
          />
        </div>
      </div>
    </div>
  )

  const renderGroup = (group) => (
    <div key={group.group_id} className="col-xl-12 mb-4">
      <div className="card shadow-none">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h4 className="card-title mb-0">{group.group_name}</h4>
        </div>
        <div className="card-body ">
          <div className="row">{group.questions.map((question) => renderQuestion(question))}</div>
        </div>
      </div>
    </div>
  )

  return (
    <div className="mb-4 border rounded p-3 shadow-sm bg-white mt-4">
      {/* <div className="row align-items-center mb-3">
        <div className="col-md-6 d-flex align-items-center gap-3">
          <div className="form-check form-switch m-0">
            <input
              className="form-check-input"
              type="checkbox"
              id="followupTypeSwitch"
              checked={isScheduledFollowup}
              onChange={() => setIsScheduledFollowup(!isScheduledFollowup)}
            />
          </div>
          <label
            className="form-check-label mb-0"
            htmlFor="followupTypeSwitch"
            style={{ minWidth: "180px", whiteSpace: "nowrap" }}
          >
            {isScheduledFollowup ? "Scheduled Followup" : "Intermediate Followup"}
          </label>

          {isScheduledFollowup && (
            <div style={{ flex: 1 }}>
              <Select
                value={selectedScheduleReason}
                onChange={setSelectedScheduleReason}
                options={followupOptions}
                isClearable
                placeholder="Select Schedule Follow-Up"
                className="react-select-container"
                classNamePrefix="react-select"
                components={{
                  Option: CustomOption,
                  SingleValue: CustomSingleValue,
                }}
                styles={{
                  control: (provided) => ({
                    ...provided,
                    minHeight: "50px",
                    height: "50px",
                  }),
                  singleValue: (provided) => ({
                    ...provided,
                    lineHeight: "50px",
                  }),
                  dropdownIndicator: (provided) => ({
                    ...provided,
                    padding: "4px",
                  }),
                  indicatorSeparator: (provided) => ({
                    ...provided,
                    display: "none",
                  }),
                }}
              />
            </div>
          )}
        </div>
      </div> */}

      <h5 className="mb-3">Follow-up Form</h5>

      {standaloneQuestions.length > 0 && (
        <div className="mb-4">
          <div className="row">{standaloneQuestions.map(renderQuestion)}</div>
        </div>
      )}

      {groupedQuestions.length > 0 && (
        <div className="mb-4">
          <div className="row">{groupedQuestions.map(renderGroup)}</div>
        </div>
      )}

      {notes.length > 0 && (
        <div className="mt-4">
          {notes.map((note, index) => (
            <div
              key={index}
              className="mb-2 custom-timeline-panel timeline-panel bgl-dark flex-wrap border-0 p-3 rounded"
            >
              <h6 className="font-weight-bold">{note?.note_description}</h6>
              <p className="fs-15 mb-0">Date: {note?.created_at}</p>
              {note?.is_important === "Yes" && <span className="badge bg-warning me-1">Important</span>}
              {note?.is_emergency === "Yes" && <span className="badge bg-danger">Emergency</span>}
            </div>
          ))}
        </div>
      )}

      <div className="d-flex justify-content-end gap-3 mt-4">
        <button
          type="button"
          className="btn btn-sm btn-outline-primary px-4 py-2"
          onClick={() => setShowNotesModal(true)}
        >
          Add Notes
        </button>
        <button
          type="button"
          className="btn btn-outline-danger px-4 py-2"
          onClick={handleDiscard}
          disabled={addNoteLoading}
        >
          Discard
        </button>
        <button type="submit" className="btn btn-primary px-4 py-2" onClick={handleSubmit} disabled={addNoteLoading}>
          {addNoteLoading ? (
            <>
              <i className="fas fa-spinner fa-spin me-2"></i>
              Adding Follow-Up...
            </>
          ) : (
            "Save"
          )}
        </button>
      </div>

      {showNotesModal && (
        <Modal
          show={showNotesModal}
          onHide={() => setShowNotesModal(false)}
          backdrop="static"
          keyboard={false}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Add Notes</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="mb-3">
              <label className="form-label">Note</label>
              <textarea
                className="form-control"
                rows="3"
                value={noteText}
                placeholder="Enter the Notes here"
                onChange={(e) => setNoteText(e.target.value)}
              />
            </div>

            <div className="form-check d-flex align-items-center mb-2">
              <input
                className="form-check-input"
                type="checkbox"
                id="importantCheck"
                checked={isImportant}
                onChange={(e) => setIsImportant(e.target.checked)}
              />
              <label className="form-check-label ms-2" htmlFor="importantCheck">
                Important
              </label>
            </div>
            <div className="form-check d-flex align-items-center mb-3">
              <input
                className="form-check-input"
                type="checkbox"
                id="emergencyCheck"
                checked={isEmergency}
                onChange={(e) => setIsEmergency(e.target.checked)}
              />
              <label className="form-check-label ms-2" htmlFor="emergencyCheck">
                Emergency
              </label>
            </div>

            {(isImportant || isEmergency) && (
              <div className="mb-3">
                <label className="form-label">Select Doctor</label>
                <Select
                  value={selectedDoctorForNote}
                  onChange={setSelectedDoctorForNote}
                  options={staticDoctors.map((doctor) => ({
                    value: doctor.doctor_id,
                    label: doctor.doctor_name,
                    specialization: doctor.specialization,
                  }))}
                  formatOptionLabel={formatOptionLabel}
                  isClearable={true}
                  isSearchable
                  placeholder="Select a doctor"
                  className="basic-single"
                  classNamePrefix="select"
                  noOptionsMessage={() => "No doctors found"}
                />
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <button className="btn btn-outline-danger" onClick={() => setShowNotesModal(false)}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSaveNote} disabled={addNoteLoading}>
              {addNoteLoading ? (
                <>
                  <i className="fas fa-spinner fa-spin me-2"></i>
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  )
}

FollowUpForm.propTypes = {
  showFollowUpForm: PropTypes.bool.isRequired,
  setShowFollowUpForm: PropTypes.func.isRequired,
  followupOptions: PropTypes.array.isRequired,
  CustomOption: PropTypes.func.isRequired,
  CustomSingleValue: PropTypes.func.isRequired,
  prescriptionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  patientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
}

export default FollowUpForm;
